import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  GetWorkspaceIssueSourceSettingsDocument,
  type IssueSourceSettings,
  UpdateWorkspaceDocument,
} from "#app/lib/data";

// `IssueSourceSettings` is a row alias over the fragment on the schema type of
// the same name.
export type { IssueSourceSettings };

const DEFAULT_SETTINGS: IssueSourceSettings = {
  __typename: "IssueSourceSettings",
  requireIssueSource: false,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIssueSourceSettings() {
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const queryVars = useMemo(() => ({ id: workspaceId! }), [workspaceId]);

  const { data, loading, error } = useQuery(
    GetWorkspaceIssueSourceSettingsDocument,
    {
      variables: queryVars,
      skip: !workspaceId,
      fetchPolicy: "cache-and-network",
    },
  );

  const [updateWorkspaceMutation] = useMutation(UpdateWorkspaceDocument);

  const settings = useMemo<IssueSourceSettings>(
    () => data?.workspace?.issueSourceSettings ?? DEFAULT_SETTINGS,
    [data],
  );

  const updateSetting = useCallback(
    async (field: keyof IssueSourceSettings, value: boolean) => {
      if (!workspaceId) return;
      try {
        await updateWorkspaceMutation({
          variables: {
            id: workspaceId,
            input: {
              issueSourceSettings: {
                [field]: value,
              },
            },
          },
        });
        toastManager.add({ title: "Setting updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update setting");
      }
    },
    [workspaceId, updateWorkspaceMutation],
  );

  return {
    settings,
    hasData: !!data?.workspace,
    isLoading: loading,
    error,
    updateSetting,
  };
}
